import type { Question } from '@app/domain/instrument'
import type {
  IEntry,
  IEntryAuthentication,
} from '@app/domain/instrument/instrument.interface'
import type { IProgramInteraction } from '@app/domain/program/program.interface'
import type {
  IVisit,
  IVisitDefinition,
  IDataInstrument,
  VisitStatusId,
  IWorkflowStep,
  PdfTemplate,
} from '@app/domain/visit/visit.interface'
import type { IApiPatient} from '@app/state/patients/helpers';
import { convertApiPatient } from '@app/state/patients/helpers'

export interface IApiAuthentication {
  id: string
  entryId: string
  patientMRN: string
  patientName: string
  patientSignature: string
  signedOn: string
  witnessName?: string
  witnessId?: string
  witnessSignature?: string
  witnessedOn?: string
}

export interface IApiEntry {
  id: string
  visitId: string
  instrumentId: string
  instrumentVersion: number
  derivedValues?: Record<string, unknown>
  responses?: Record<string, string>
  authentication?: IApiAuthentication
  updatedAt?: string
}

export function convertApiAuthentication(
  auth: IApiAuthentication,
): IEntryAuthentication {
  return {
    id: auth.id,
    entryId: auth.entryId,
    patientMRN: auth.patientMRN,
    patientName: auth.patientName,
    patientSignature: auth.patientSignature,
    signedOn: new Date(auth.signedOn),
    witnessName: auth.witnessName,
    witnessId: auth.witnessId,
    witnessSignature: auth.witnessSignature,
    witnessedOn: auth.witnessedOn ? new Date(auth.witnessedOn) : null,
  }
}

export function convertApiEntry(entry: IApiEntry): IEntry {
  return {
    id: entry.id,
    visitId: entry.visitId,
    instrumentId: entry.instrumentId,
    instrumentVersion: entry.instrumentVersion,
    responses: entry.responses,
    authentication: entry.authentication
      ? convertApiAuthentication(entry.authentication)
      : null,
    updatedAt: entry.updatedAt ? new Date(entry.updatedAt) : null,
    derivedValues: entry.derivedValues,
  }
}

export interface IApiDefinition {
  id: string
  name: string
  version: number
  isSignatureRequired: boolean
  isWitnessSignatureRequired: boolean
  pdfTemplates: PdfTemplate[]
}

export interface IApiVisit {
  id: string
  definition: IApiDefinition
  patientId?: string
  location: string
  startTime: string
  status: string
  state: IApiVisitState
}

export interface IApiVisitState {
  instrument?: string
  screen?: number
  stage?: string
}

export interface IApiUpdateVisit {
  definitionId: string
  location: string
  startTime: string
  visitStatus: string
}

export interface IApiWorkflowStep {
  instrumentId: string
  order: number
  workflowLogic?: string
}

export interface IApiVisitDefinition {
  allInstruments: Array<IApiDataInstrument>
  allWorkflow: Array<IApiWorkflowStep>
  id: string
  version: number
  name: string
  programInteractions?: IProgramInteraction[]
  visitInstruments: Array<IApiDataInstrument>
  visitWorkflow: Array<IApiWorkflowStep>
  isSignatureRequired: boolean
  isWitnessSignatureRequired: boolean
  pdfTemplates: PdfTemplate[]
}

export enum VisitUseCase {
  Clinic = 'clinic',
  Intake = 'intake',
}

export interface IScreen {
  position: number
}

export interface IView {
  id: string
  useCase: VisitUseCase
  screens: Array<IScreen>
}

export interface IApiDataInstrument {
  id: string
  label: string
  type: string
  version: number
  questions?: Record<string, Question>
  views?: Array<IView>
}

export function convertApiVisit(
  visit: IApiVisit,
  patient?: IApiPatient,
): IVisit {
  const domainPatient = patient ? convertApiPatient(patient) : undefined
  return {
    id: visit.id,
    location: visit.location,
    startTime: new Date(visit.startTime),
    status: visit.status as VisitStatusId,
    definition: visit.definition,
    state: {
      ...visit.state,
      instrumentId: visit.state.instrument,
    },
    patient: domainPatient,
  }
}

export function convertApiVisitDefinition(
  definition: IApiVisitDefinition,
): IVisitDefinition {
  let numScreens = 0
  const workflow = definition.allWorkflow
    ? definition.allWorkflow.sort((s1, s2) => s1.order - s2.order)
    : definition.allWorkflow
  const instruments = definition.allInstruments
  if (instruments !== null) {
    instruments.forEach((instrument) => {
      if (instrument.views) {
        instrument.views
          .filter((view) => view.useCase === 'intake')
          .forEach((view) => {
            numScreens += view.screens.length
            view.screens.sort((s1, s2) => s1.position - s2.position)
          })
      }
    })
  }
  return {
    id: definition.id,
    version: definition.version,
    name: definition.name,
    allInstruments: instruments as IDataInstrument[],
    allWorkflow: workflow as IWorkflowStep[],
    totalLength: numScreens,
    programInteractions: definition.programInteractions,
    isSignatureRequired: definition.isSignatureRequired,
    isWitnessSignatureRequired: definition.isWitnessSignatureRequired,
    pdfTemplates: definition.pdfTemplates,
  }
}
